import React, { useEffect, useState, useRef } from 'react';
import { Box, Avatar, Button, CircularProgress, Alert } from '@mui/material';
import { fileService } from '../../services/file.service';

interface AvatarUploaderProps {
  currentAvatarUrl: string | null | undefined;
  onUploadSuccess: (newAvatarPath: string) => void;
}

export const AvatarUploader: React.FC<AvatarUploaderProps> = ({ currentAvatarUrl, onUploadSuccess }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null | undefined>(currentAvatarUrl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPreviewUrl(currentAvatarUrl);
  }, [currentAvatarUrl]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Непідтримуваний тип файлу. Дозволені: JPEG, PNG, WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) { // 5MB
      setError('Розмір файлу не повинен перевищувати 5MB.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fileService.uploadAvatar(file);
      if (response.path) {
        setPreviewUrl(response.path);
        onUploadSuccess(response.path);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Не вдалося завантажити аватар.';
      setError(errorMessage);
      console.error(err);
    } finally {
      setLoading(false);
      if(fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mb: 3 }}>
      <Avatar src={previewUrl || undefined} sx={{ width: 120, height: 120 }} />
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
      />
      <Button variant="outlined" onClick={handleButtonClick} disabled={loading}>
        {loading ? <CircularProgress size={24} /> : 'Змінити аватар'}
      </Button>
      {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
    </Box>
  );
};
